<template>
	<div class="w-full p-6 print:py-3">
		<h2
			class="form-title pb-6 text-2xl font-bold print:mb-2 print:border-b-2 print:border-slate-900 print:pt-2 print:pb-3 print:text-sm/0"
		>
			{{ t('plan.sectionTitle.funeralServicePreferences') }}
		</h2>

		<CopyButtonList
			:field-or-section="[
				'funeralHomeName',
				'placeOfService',
				'placeOfGathering',
				'religiousPreference',
				'serviceOrganization',
				'postServiceMeal',
			]"
			:active-index="activePlanIndex"
			class="print:hidden"
		/>

		<section>
			<div
				class="section-grid-row grid grid-cols-2 gap-2 *:w-full *:max-w-none"
			>
				<FormKit
					type="select"
					name="funeralHomeId"
					:label="t('plan.funeralHomeName')"
					:placeholder="
						t('plan.placeholders.select', {
							placeholderLabel: t('plan.funeralHomeName'),
						})
					"
					:disabled="
						funeralHomeOptions && Object.keys(funeralHomeOptions).length === 1
					"
					:options="funeralHomeOptions"
				/>

				<FormKit
					type="textarea"
					auto-height
					name="placeOfService"
					:label="t('plan.placeOfService')"
				/>
			</div>
			<div
				class="section-grid-row grid grid-cols-4 gap-2 *:w-full *:max-w-none"
			>
				<FormKit
					type="textarea"
					auto-height
					name="placeOfGathering"
					:label="t('plan.placeOfGathering')"
				/>

				<FormKit
					type="textarea"
					auto-height
					name="religiousPreference"
					:label="t('plan.religiousPreference')"
				/>

				<FormKit
					type="textarea"
					auto-height
					name="serviceOrganization"
					:label="t('plan.serviceOrganization')"
					:placeholder="t('plan.placeholders.organizationExamples')"
				/>

				<div
					:class="{ 'mb-0 flex items-center': !activePlan?.postServiceMeal }"
				>
					<FormKit
						v-if="activePlan?.postServiceMeal"
						type="textarea"
						auto-height
						name="postServiceMealDetails"
						:label="t('plan.postServiceMealDetails')"
						:placeholder="t('plan.placeholders.postServiceMealDetails')"
					/>
					<FormKit
						type="checkbox"
						name="postServiceMeal"
						:label="t('plan.postServiceMeal')"
						outer-class="mb-0!"
					/>
				</div>
			</div>
			<div
				class="section-grid-row grid grid-cols-1 *:w-full *:max-w-none *:px-1"
				:class="{ 'print:hidden': !activePlan?.serviceNotes?.trim() }"
			>
				<FormKit
					type="textarea"
					auto-height
					name="serviceNotes"
					:label="t('plan.notes')"
					:outer-class="{ 'mb-0': true }"
					:placeholder="t('plan.placeholders.serviceNotes')"
					validation="length:0,10000"
					validation-visibility="live"
				/>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import CopyButtonList from '@/components/presentation/CopyButtonList.vue'
import { DeckFlavor } from '@/enums/Language'
import { usePresentationLocale } from '@/i18n'
import { useContentStore } from '@/stores/content'
import { useSessionStore } from '@/stores/session'
import { FormKit } from '@formkit/vue'
import { onMounted, ref } from 'vue'

const { t } = usePresentationLocale()
const content = useContentStore()
const sessionStore = useSessionStore()

defineProps<{
	activePlanIndex: number
	activePlan: Plan | null
}>()

let funeralHomeOptions = ref<Record<string, string>>({})

onMounted(async () => {
	if (!sessionStore.activePriceBook?.data.id) {
		const fhName = sessionStore.activeFh?.name!
		const fhId: string | undefined = sessionStore.activeFh?.id!
		funeralHomeOptions.value = {
			[fhId]: fhName,
		}
		return
	}

	try {
		const fhs = await content.getFuneralHomeLocations(
			sessionStore.activeSession?.language || DeckFlavor.Default,
			{
				crmGroupId: sessionStore.activeSession?.crmFhGroupId,
				matchPriceBookId: sessionStore.activePriceBook?.data.id,
			}
		)

		funeralHomeOptions.value = fhs.reduce(
			(acc, fh) => ({
				...acc,
				[fh.id]: fh.name,
			}),
			{}
		)
	} catch (error) {
		console.error('Failed to fetch funeral homes', error)
	}
})
</script>
